import { type ReactNode, useEffect, useRef } from 'react';
import {
  useForm,
  useFormState,
  type Control,
  type DefaultValues,
  type FieldValues,
  type Path,
  type UseFormReturn
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { $ZodType } from 'zod/v4/core';
import { type AppIcon } from '@/constants/entityIcons';
import { useTranslation } from 'react-i18next';
import {
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  FormDialogShell,
  type DialogShellSize,
  type FormDialogShellRenderArgs
} from '@bliks-io/ui';
import { CrudErrorAlert } from '@/components/crud';
import { extractDirtyFields } from '@/lib/form-utils';
import { applyApiErrorToForm } from '@/lib/errors';
import type { ApiError } from '@/lib/agent.types';
import { logger } from '@/lib/logger';

/**
 * Leaf so the `root` subscription stays out of FormDialog: reading
 * `formState.errors` there would re-render every dialog body on any field's
 * error change.
 */
function RootErrorAlert({
  control,
  onDismiss
}: {
  control: Control<FieldValues>;
  onDismiss: () => void;
}) {
  const { errors } = useFormState({ control, name: 'root' });
  const message = errors.root?.message;
  return message ? <CrudErrorAlert error={message} onDismiss={onDismiss} /> : null;
}

/** Dotted paths of every scalar in a form's default values (`contact.email`). */
function leafPaths(values: object, prefix = ''): string[] {
  return Object.entries(values).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const isPlainObject =
      value !== null &&
      typeof value === 'object' &&
      Object.getPrototypeOf(value) === Object.prototype;
    return isPlainObject ? leafPaths(value as object, path) : [path];
  });
}

export interface FormDialogChildrenArgs<
  TFormInput extends FieldValues,
  TFormOutput extends FieldValues
> {
  form: UseFormReturn<TFormInput, object, TFormOutput>;
}

export interface FormDialogProps<
  TFormInput extends FieldValues,
  TFormOutput extends FieldValues = TFormInput
> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  icon: AppIcon;
  defaultValues: TFormInput;
  /** Optional zod schema. When provided, drives validation via `zodResolver`. */
  schema?: $ZodType<TFormOutput, TFormInput>;
  /**
   * Submit handler. When editing, receives only the dirty subset of fields;
   * when creating, receives the full output. When `schema` is omitted, the
   * type collapses to `TFormInput`.
   *
   * Receives the form instance as its second argument for the cases that need
   * the complete state (full-value derivation, cross-field validation). It is
   * awaited, so a returned promise drives the submit spinner via
   * `formState.isSubmitting`, and a rejection is routed onto the form: field
   * errors inline, everything else into the alert above the body. The resolved
   * value is passed through untouched, so a handler can chain follow-up work
   * on it.
   */
  onSubmit: (data: TFormOutput, form: UseFormReturn<TFormInput, object, TFormOutput>) => unknown;
  /** Render-prop body. Receives the internal form instance. */
  children: (args: FormDialogChildrenArgs<TFormInput, TFormOutput>) => ReactNode;
  /** Edit vs create. Controls default submit label (`common.save` vs `common.create`). */
  isEditing?: boolean;
  /** Submit-button label override. */
  submitLabel?: string;
  /** Cancel-button label override. */
  cancelLabel?: string;
  /**
   * Additional dirty signal, OR-ed with `form.formState.isDirty`. Provide this
   * when the dialog tracks state outside the form (e.g. file uploads) that
   * should count as dirty, so neither the unsaved-changes prompt nor the
   * edit-mode no-op gate below misses it.
   */
  extraDirty?: boolean;
  /**
   * Field paths eligible for an inline error. A 422 naming anything else goes
   * to the root alert rather than `setError`-ing an input that is not on
   * screen, where the message would never be seen. Defaults to the leaf paths
   * of `defaultValues` — declare it when the body hides some of those behind a
   * condition, listing only what it always renders.
   *
   * Declared rather than derived from the registry: react-hook-form exposes no
   * public account of which inputs are mounted, and the private one it does
   * have (`control._fields`) is free to change shape in a minor release.
   */
  fields?: Path<TFormInput>[];
  size?: DialogShellSize;
}

/**
 * Form dialog wrapper built on `FormDialogShell`. Owns the `useForm` instance,
 * the open-reset effect, dirty-field extraction on edit, the unsaved-changes
 * prompt, the submit lifecycle (spinner + error routing), and the standard
 * cancel/submit footer.
 */
export function FormDialog<
  TFormInput extends FieldValues,
  TFormOutput extends FieldValues = TFormInput
>({
  open,
  onOpenChange,
  title,
  description,
  icon: IconComponent,
  defaultValues,
  schema,
  onSubmit,
  children,
  isEditing = false,
  submitLabel,
  cancelLabel,
  fields,
  extraDirty = false,
  size = 'lg'
}: FormDialogProps<TFormInput, TFormOutput>) {
  const { t } = useTranslation();

  const form = useForm<TFormInput, object, TFormOutput>({
    resolver: schema ? zodResolver(schema) : undefined,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: defaultValues as DefaultValues<TFormInput>
  });

  const { handleSubmit, reset, formState, clearErrors, setError } = form;
  const { isSubmitting } = formState;
  const isDirty = formState.isDirty || extraDirty;

  const prevOpenRef = useRef(false);
  const defaultValuesRef = useRef(defaultValues);
  const resetRef = useRef(reset);
  defaultValuesRef.current = defaultValues;
  resetRef.current = reset;

  useEffect(
    function resetFormWhenDialogOpens() {
      if (open && !prevOpenRef.current) {
        resetRef.current(defaultValuesRef.current as DefaultValues<TFormInput>);
      }
      prevOpenRef.current = open;
    },
    [open]
  );

  const handleValidSubmit = async (data: TFormOutput) => {
    if (isEditing && !isDirty) {
      onOpenChange(false);
      return;
    }
    const payload = isEditing
      ? (extractDirtyFields(
          data as unknown as Record<string, unknown>,
          formState.dirtyFields as Partial<Record<string, boolean | object>>
        ) as TFormOutput)
      : data;

    try {
      await onSubmit(payload, form);
    } catch (error) {
      logger.error('Form dialog submit failed:', error);
      applyApiErrorToForm(error as ApiError, setError, t, {
        fields: fields ?? (leafPaths(defaultValues) as Path<TFormInput>[])
      });
    }
  };

  const resolvedSubmitLabel = submitLabel ?? (isEditing ? t('common.save') : t('common.create'));
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');

  return (
    <FormDialogShell
      open={open}
      onOpenChange={onOpenChange}
      isDirty={isDirty}
      isPending={isSubmitting}
      size={size}
    >
      {({ close }: FormDialogShellRenderArgs) => (
        <form
          noValidate
          onSubmit={handleSubmit(handleValidSubmit)}
          className="flex flex-1 min-h-0 flex-col"
        >
          <DialogShellHeader
            icon={<IconComponent className="h-5 w-5" />}
            title={title}
            subtitle={description}
            onClose={close}
          />
          <DialogShellBody>
            <RootErrorAlert
              control={form.control as unknown as Control<FieldValues>}
              onDismiss={() => clearErrors('root')}
            />
            {children({ form })}
          </DialogShellBody>
          <DialogShellFooter
            secondary={{
              label: resolvedCancelLabel,
              onClick: close,
              disabled: isSubmitting
            }}
            primary={{
              label: resolvedSubmitLabel,
              type: 'submit',
              loading: isSubmitting
            }}
          />
        </form>
      )}
    </FormDialogShell>
  );
}
